/**
 * @file Workspace creation form (T0.7).
 *
 * Shown only when the signed-in user belongs to zero workspaces. The name rule
 * mirrors the server: `POST /workspaces` trims and rejects an empty name
 * (apps/api/src/routes/workspaces.ts), so the form trims before submitting and
 * refuses to send a blank. The server remains authoritative — this is only to
 * avoid a pointless round trip.
 */
import { useState } from "react";
import { Button, Card } from "./ui";

export function WorkspaceCreateForm({
  onCreate,
  isSubmitting,
  error,
}: {
  readonly onCreate: (name: string) => void;
  readonly isSubmitting: boolean;
  readonly error: Error | null;
}) {
  const [name, setName] = useState("");
  const trimmedName = name.trim();
  const canSubmit = trimmedName.length > 0 && !isSubmitting;

  return (
    <Card>
      <h1 className="text-xl font-semibold tracking-tight">Create your workspace</h1>
      <p className="mt-2 text-sm text-[var(--color-ink-muted)]">
        A workspace holds your properties, leases and people. You will be its owner.
      </p>

      <form
        className="mt-6 space-y-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (canSubmit) {
            onCreate(trimmedName);
          }
        }}
      >
        <label htmlFor="workspace-name" className="block text-sm font-medium">
          Workspace name
        </label>
        <input
          id="workspace-name"
          name="workspace-name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          maxLength={120}
          required
          autoComplete="organization"
          placeholder="e.g. Maple Street Rentals"
          className="w-full rounded-lg border border-[var(--color-line)] px-3 py-2 text-sm outline-none focus:border-[var(--color-brand)]"
        />
        <Button type="submit" disabled={!canSubmit}>
          {isSubmitting ? "Creating…" : "Create workspace"}
        </Button>
      </form>

      {error !== null ? (
        <p role="alert" className="mt-4 text-sm text-[var(--color-danger)]">
          {error.message}
        </p>
      ) : null}
    </Card>
  );
}
